import React from "react";
import styled from "styled-components";
import { designTokens, getTypography } from "styles/designTokens";

const FooterContainer = styled.footer`
  width: 100%;
  background: #fff;
  border-top: 1px solid rgba(75, 94, 255, 0.08);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 80px 0px 40px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 60px 0px 30px;
  }

  @media (max-width: 480px) {
    padding: 40px 0px 20px;
  }
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  padding: 0 400px;
  margin-bottom: 60px;
  gap: 40px;

  @media (max-width: 1400px) {
    padding: 0 200px;
  }

  @media (max-width: 1200px) {
    padding: 0 100px;
  }

  @media (max-width: 768px) {
    padding: 0 40px;
    flex-direction: column;
    gap: 30px;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
    gap: 24px;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const CompanyName = styled.h3`
  ${getTypography("korean", "body1")}
  font-weight: 600;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CompanyDescription = styled.p`
  color: ${designTokens.colors.font.quaternary};
  font-size: 15px;
  line-height: 1.8;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const MenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  a {
    ${getTypography("korean", "body2")}
    color: #444;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #5c6eff;
    }

    @media (max-width: 768px) {
      font-size: 14px;
    }

    @media (max-width: 480px) {
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(75, 94, 255, 0.08);
  margin: 40px 0;

  @media (max-width: 768px) {
    margin: 30px 0;
  }

  @media (max-width: 480px) {
    margin: 24px 0;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 0 400px;
  width: 100%;
  color: ${designTokens.colors.font.quaternary};
  font-size: 14px;

  @media (max-width: 1400px) {
    padding: 0 200px;
  }

  @media (max-width: 1200px) {
    padding: 0 100px;
  }

  @media (max-width: 768px) {
    padding: 0 40px;
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
    font-size: 13px;
    gap: 16px;
  }
`;

const PolicyLinks = styled.div`
  display: flex;
  gap: 40px;

  a {
    color: ${designTokens.colors.font.quaternary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
      color: #5c6eff;
    }
  }

  @media (max-width: 768px) {
    gap: 20px;
    justify-content: center;
  }

  @media (max-width: 480px) {
    gap: 16px;
    flex-direction: column;
    align-items: center;
  }
`;

const FooterSection: React.FC = () => {
  return (
    <FooterContainer>
      <TopSection>
        <CompanyInfo>
          <CompanyName>주식회사 마고</CompanyName>
          <CompanyDescription>
            비즈니스를 위한 최고의 음성 AI 파트너.
            <br />
            오디온과 함께 더 나은 음성 경험을 만들어보세요.
          </CompanyDescription>
          <a href="mailto:contact@holamago.com" style={{ color: "#4b5eff", textDecoration: "none", fontWeight: 500 }}>
            contact@holamago.com
          </a>
        </CompanyInfo>

        <MenuLinks>
          <a href="https://www.holamago.com/" target="_blank" rel="noopener noreferrer">회사 소개</a>
          <a href="https://mago-1.gitbook.io/audion-python-sdk-v2" target="_blank" rel="noopener noreferrer">SDK로 시작하기</a>
          <a href="/contact" target="_blank">문의하기</a>
        </MenuLinks>
      </TopSection>

      <Divider />

      <BottomSection>
        <span>© {new Date().getFullYear()} MAGO Inc. All Rights Reserved.</span>
        <PolicyLinks>
          <a href="/terms" target="_blank">서비스 이용약관</a>
          <a href="/privacy" target="_blank">개인정보처리방침</a>
        </PolicyLinks>
      </BottomSection>
    </FooterContainer>
  );
};

export default FooterSection;
